import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CreateDeliverablePopup.module.scss';
import {
  IconChevronDown,
  IconPlus,
  IconCheck,
  IconCalendar,
  IconClipboardList,
  IconChecklist,
  IconUserCheck,
  IconCalendarEvent,
} from '@tabler/icons-react';
import { DeliverObjectCriticality, PhaseDto, DeliverObjectDto, ProjectDto, CatalogDTO, TaskDto, CheckPointDto, Status, PhaseType } from '../../../api/model';
import { getPhaseLabel } from '../../../utils/phaseUtils';
import { usePostDeliverObjectCreate } from '../../../hooks/usePostDeliverObjectCreate';
import { useNotifications } from '../../../hooks/useNotifications';
import { getApiErrorMessage } from '../../../hooks/useApiMutation';
import { useQueryClient } from '@tanstack/react-query';
import { useProjectReleases } from '../../../hooks/useProjectReleases';
import { useGetCatalogFilter } from '../../../api/catalog-api/catalog-api';
import { usePostTask } from '../../../api/task-api/task-api';
import { usePostCheckpoint } from '../../../api/check-point-api/check-point-api';


interface CreateDeliverablePopupProps {
  isOpen: boolean;
  onClose: () => void;
  phases?: PhaseDto[];
  existingDeliverables?: { id: string; name: string }[];
  releases?: { id: string; name: string; date: string }[];
  projectId?: number;
  project?: ProjectDto;
}

const CreateDeliverablePopup: React.FC<CreateDeliverablePopupProps> = ({ 
  isOpen, 
  onClose,
  phases = [],
  existingDeliverables = [],
  releases = [],
  projectId,
  project
}) => {
  const { t, i18n } = useTranslation('deliverables');
  const lang: 'en' | 'de' = i18n.language?.toLowerCase().startsWith('de') ? 'de' : 'en';

  // Resolve language split catalog fields for the active locale
  const catDeliverableName = (item: CatalogDTO) => lang === 'de' ? item.deliverableNameDe : item.deliverableNameEn;
  const catTaskName = (item: CatalogDTO) => lang === 'de' ? item.taskNameDe : item.taskNameEn;
  const catValidationCriteria = (item: CatalogDTO) => lang === 'de' ? item.validationCriteriaDe : item.validationCriteriaEn;
  const catAcceptanceCriteria = (item: CatalogDTO) => lang === 'de' ? item.acceptanceCriteriaDe : item.acceptanceCriteriaEn;
  const catValidationMethods = (item: CatalogDTO) => lang === 'de' ? item.validationMethodsDe : item.validationMethodsEn;
    const catResponsibleRoles = (item: CatalogDTO) => lang === 'de'
        ? item.responsibleRolesDe || item.responsibleRoles
        : item.responsibleRoles || item.responsibleRolesDe;
  const firstInputRef = useRef<HTMLSelectElement>(null);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotifications();

  const [mode, setMode] = useState<'custom' | 'preset'>('custom');

  const [presetStep, setPresetStep] = useState<'phase' | 'deliverable' | 'dates' | 'criticality' | 'review'>('phase');
  const [selectedPhaseType, setSelectedPhaseType] = useState<PhaseType | ''>('');
  const [selectedDeliverableName, setSelectedDeliverableName] = useState('');
  const [isExtendedCatalog, setIsExtendedCatalog] = useState(false);
  const [catalogData, setCatalogData] = useState<CatalogDTO[]>([]);
  const [filteredCatalogItems, setFilteredCatalogItems] = useState<CatalogDTO[]>([]);
  const [presetDates, setPresetDates] = useState({ startDate: '', endDate: '' });
  const [presetCriticality, setPresetCriticality] = useState<DeliverObjectCriticality>(DeliverObjectCriticality.MIDDLE);
  const [taskValidationDates, setTaskValidationDates] = useState<Record<string, string>>({});
  const [isCreatingPreset, setIsCreatingPreset] = useState(false);

  const shouldFetchCatalogNames = isOpen && mode === 'preset' && project?.complexity && project?.method && project?.projectTypeUid && selectedPhaseType;
  
  const apiParamsForNames = {
    complexity: project?.complexity!,
    methodology: project?.method!,
    phase: selectedPhaseType as PhaseType,
    projectType: project?.projectTypeUid!,
    ...(isExtendedCatalog ? { extended: true } : {})
  };
  
  const { data: catalogNamesResponse } = useGetCatalogFilter(
    apiParamsForNames,
    {
      query: {
        enabled: !!shouldFetchCatalogNames,
        staleTime: 1000 * 60 * 5,
      }
    }
  );

  const shouldFetchCatalogDetails = isOpen && mode === 'preset' && project?.complexity && project?.method && project?.projectTypeUid && selectedPhaseType && selectedDeliverableName;
  
  const apiParamsForDetails = {
    complexity: project?.complexity!,
    methodology: project?.method!,
    phase: selectedPhaseType as PhaseType,
    projectType: project?.projectTypeUid!,
    deliverObjectName: selectedDeliverableName,
    ...(isExtendedCatalog ? { extended: true } : {})
  };
  
  const { data: catalogDetailsResponse, isLoading: isLoadingCatalog, error: catalogError } = useGetCatalogFilter(
    apiParamsForDetails,
    {
      query: {
        enabled: !!shouldFetchCatalogDetails,
        staleTime: 0,
      }
    }
  );

  // mutations of tasks and checkpoints
  const createTaskMutation = usePostTask();
  const createCheckpointMutation = usePostCheckpoint();
  
  useEffect(() => {
    if (catalogNamesResponse && mode === 'preset') {
      setCatalogData(catalogNamesResponse);
    }
  }, [catalogNamesResponse, mode]);

  useEffect(() => {
    if (catalogDetailsResponse && selectedDeliverableName) {
      setFilteredCatalogItems(catalogDetailsResponse);
    }
  }, [catalogDetailsResponse, selectedDeliverableName]);
  
  // Get deliverable names from catalog and make them as uqnie
  const uniqueDeliverableNames = useMemo(() => {
    if (!catalogData.length) {
      return [];
    }
    const names = new Set(catalogData.map(item => catDeliverableName(item)).filter(Boolean));
    return Array.from(names);
  }, [catalogData, lang]);
  
  // Fetch project releases
  const { data: projectReleases = [], isLoading: isLoadingReleases, refetch: refetchReleases } = useProjectReleases(projectId);
  
  
  const createDeliverableMutation = usePostDeliverObjectCreate({
    mutation: {
      onSuccess: (data: DeliverObjectDto) => {
        showSuccess(t('create.successTitle'), `"${data.name}" ${t('create.successMessage')}`);
        
        // query invalidations to refresh data
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['/project/findbyid'] });
        queryClient.invalidateQueries({ queryKey: ['/deliver-object'] });
        
        if (projectId) {
          queryClient.invalidateQueries({ queryKey: [`/phase/${projectId}/project`] });
        }

        if (formData.phaseId) {
          const phaseIdNum = parseInt(formData.phaseId);
          if (!isNaN(phaseIdNum)) {
            queryClient.invalidateQueries({ queryKey: [`/deliver-object/${phaseIdNum}/phase`] });
          }
        }
        
        onClose();
      },
      onError: (error: any) => {
        showError(t('create.errorTitle'), getApiErrorMessage(error));
      },
    },
  });
  
  const [formData, setFormData] = useState({
    name: '',
    phaseId: '',
    criticality: 'EASY' as DeliverObjectCriticality,
    startDate: '',
    endDate: '',
    isExtended: false,
    releaseId: '',
  });

  // criticality options
  const criticalityOptions = Object.values(DeliverObjectCriticality);

  const selectedCustomPhase = useMemo(() => {
    if (formData.phaseId) {
      return phases.find(p => p.id?.toString() === formData.phaseId);
    }
    return undefined;
  }, [formData.phaseId, phases]);

  const selectedPresetPhase = useMemo(() => {
    if (selectedPhaseType) {
      return phases.find(p => p.type === selectedPhaseType);
    }
    return undefined;
  }, [selectedPhaseType, phases]);

    const selectedPhaseLabel = useMemo(() => {
        if (selectedPresetPhase) {
            return getPhaseLabel(selectedPresetPhase, phases);
        }
        return selectedPhaseType || '';
    }, [phases, selectedPhaseType, selectedPresetPhase]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleCancel();
      }
    };

    if (isOpen) {
        // Reset all form state on every open
        setFormData({
            name: '',
            phaseId: '',
            criticality: 'EASY',
            startDate: '',
            endDate: '',
            isExtended: false,
            releaseId: ''
        });
        setMode('custom');
        setPresetStep('phase');
        setSelectedPhaseType('');
        setSelectedDeliverableName('');
        setIsExtendedCatalog(false);
        setCatalogData([]);
        setFilteredCatalogItems([]);
        setPresetDates({startDate: '', endDate: ''});
        setPresetCriticality(DeliverObjectCriticality.MIDDLE);
        setTaskValidationDates({});

      document.addEventListener('keydown', handleEscapeKey);
        document.body.style.overflow = 'hidden';

      // Focus the first input when popup opens
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
      if (projectId) {
        refetchReleases();
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, projectId, refetchReleases]);

  if (!isOpen) return null;

  const validateDates = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) {
      return { isValid: false, error: t('create.validation.datesRequired') };
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start >= end) {
      return { isValid: false, error: t('create.validation.startBeforeEnd') };
    }
    return { isValid: true, error: null };
  };

  const handleInputChange = (field: string, value: string | boolean | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isFormValid = () => {
    return (
      formData.name?.trim() !== '' &&
      formData.name.length <= 80 &&
      formData.phaseId?.trim() !== '' &&
      formData.startDate?.trim() !== '' &&
      formData.endDate?.trim() !== ''
    );
  };

  const handleSubmit = () => {
    if (!isFormValid()) {
      if (formData.name.length > 80) {
        showError(t('create.validation.error'), t('create.validation.nameMaxLength'));
      } else {
        showError(t('create.validation.error'), t('create.validation.required'));
      }
      return;
    }
    const dateValidation = validateDates(formData.startDate, formData.endDate);
    if (!dateValidation.isValid) {
      showError(t('create.validation.error'), dateValidation.error!);
      return;
    }
    const deliverableData: DeliverObjectDto & { phaseId?: number } = {
      name: formData.name,
      startDate: formData.startDate,
      endDate: formData.endDate,
      criticality: formData.criticality,
      isExtended: formData.isExtended,
      progress: 0
    };
    if (formData.phaseId) deliverableData.phaseId = parseInt(formData.phaseId);
    if (formData.releaseId) deliverableData.releaseId = parseInt(formData.releaseId);
    createDeliverableMutation.mutate({ data: deliverableData });
  };

  const handleCancel = () => {
    // Reset form and close
    setFormData({
      name: '',
      phaseId: '',
      criticality: 'EASY',
      startDate: '',
      endDate: '',
      isExtended: false,
      releaseId: '',
    });
    setMode('custom');
    setPresetStep('phase');
    setSelectedPhaseType('');
    setSelectedDeliverableName('');
    setIsExtendedCatalog(false);
    setCatalogData([]);
    setFilteredCatalogItems([]);
    setPresetDates({ startDate: '', endDate: '' });
    setTaskValidationDates({});
    onClose();
  };
  
  // Format dates for display
  const formatDisplayDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const doPresetCreate = async () => {
    setIsCreatingPreset(true);
    
    try {
      const selectedPhase = phases.find(p => p.type === selectedPhaseType);
      if (!selectedPhase?.id) {
        throw new Error('Selected phase not found');
      }

      const deliverableData: DeliverObjectDto & { phaseId?: number } = {
        name: selectedDeliverableName,
        startDate: presetDates.startDate,
        endDate: presetDates.endDate,
        criticality: presetCriticality,
        isExtended: isExtendedCatalog,
        progress: 0,
        phaseId: selectedPhase.id
      };
      
      const createdDeliverable = await createDeliverableMutation.mutateAsync({ data: deliverableData });
      
      if (!createdDeliverable?.id) {
        throw new Error('Failed to create deliverable');
      }

      for (const catalogItem of filteredCatalogItems) {
        const taskName = catTaskName(catalogItem);
        const validationCriteria = catValidationCriteria(catalogItem);
        const acceptanceCriteria = catAcceptanceCriteria(catalogItem);
        // Crucial fields must exist in the active language, otherwise skip this entry.
        if (!taskName || !validationCriteria || !acceptanceCriteria) continue;

        // Create task
        const taskData: TaskDto = {
          name: taskName,
          description: `Task for ${selectedDeliverableName}`,
          status: Status.PLANNED,
          progress: 0,
          deliverObjectId: createdDeliverable.id
        };

        const createdTask = await createTaskMutation.mutateAsync({ data: taskData });

        if (createdTask?.id) {
          // Create checkpoint for this task
            const validationDate = taskValidationDates[catalogItem.id?.toString() || ''];
            const checkpointData: CheckPointDto = {
                taskId: createdTask.id,
                status: Status.PLANNED,
                validationCriteria: validationCriteria,
                acceptanceCriteria: acceptanceCriteria,
                validationMethod: catValidationMethods(catalogItem),
                validationDate: validationDate || undefined,
                responsibleRole: catResponsibleRoles(catalogItem)
            };

          await createCheckpointMutation.mutateAsync({ data: checkpointData });
        }
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
        queryClient.invalidateQueries({ queryKey: ['/project'] }),
        queryClient.invalidateQueries({ queryKey: ['/project/findbyid'] }),
        queryClient.invalidateQueries({ queryKey: ['/deliver-object'] }),
        queryClient.invalidateQueries({ queryKey: ['/task'] }),
        queryClient.invalidateQueries({ queryKey: ['/check-point'] })
      ]);
      
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: [`/phase/${projectId}/project`] });
      }
      
      if (selectedPhase.id) {
        queryClient.invalidateQueries({ queryKey: [`/deliver-object/${selectedPhase.id}/phase`] });
      }
      
      showSuccess(
        t('create.successTitle'), 
        `"${selectedDeliverableName}" ${t('create.successMessage')}`
      );
      
      handleCancel();
    } catch (error) {
      showError(t('create.errorTitle'), getApiErrorMessage(error));
    } finally {
      setIsCreatingPreset(false);
    }
  };

  const handlePresetCreate = () => {
    if (!filteredCatalogItems.length || !presetDates.startDate || !presetDates.endDate) {
      showError(t('create.validation.error'), t('create.validation.required'));
      return;
    }
    const dateValidation = validateDates(presetDates.startDate, presetDates.endDate);
    if (!dateValidation.isValid) {
      showError(t('create.validation.error'), dateValidation.error!);
      return;
    }
    doPresetCreate();
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
        <div className={styles.popupHeader}>
          <div className={styles.headerContent}>
            <h2 className={styles.headerTitle}>{t('create.title')}</h2>
            <p className={styles.headerDescription}>
              {mode === 'custom' 
                ? t('create.descriptionCustom')
                : t('create.descriptionPreset')
              }
            </p>
          </div>
        </div>

        <div className={styles.formContent}>
          <div className={styles.modeSelection}>
            <button
              className={`${styles.modeButton} ${mode === 'custom' ? styles.modeButtonActive : ''}`}
              onClick={() => setMode('custom')}
              type="button"
            >
              <IconPlus size={18} />
              {t('create.modeButtons.custom')}
            </button>
            <button
              className={`${styles.modeButton} ${mode === 'preset' ? styles.modeButtonActive : ''}`}
              onClick={() => {
                setMode('preset');
                setPresetStep('phase');
              }}
              type="button"
            >
              <IconCheck size={18} />
              {t('create.modeButtons.preset')}
            </button>
          </div>

          <div className={styles.formBody}>
            {mode === 'custom' ? (
              // Custom Mode
              <>
                <div className={styles.inputGroup}>
                  <label className={`${styles.inputLabel} ${styles.required}`}>{t('create.fields.name').replace(' *', '')}</label>
              <div className={styles.inputField}>
                <input
                  type="text"
                  className={styles.input}
                  placeholder={t('create.fields.namePlaceholder')}
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  maxLength={80}
                />
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label className={`${styles.inputLabel} ${styles.required}`}>{t('create.fields.phase').replace(' *', '')}</label>
              <div className={styles.selectField}>
                <select
                  ref={firstInputRef}
                  className={styles.select}
                  value={formData.phaseId}
                  onChange={(e) => handleInputChange('phaseId', e.target.value)}
                >
                  <option value="">{t('create.fields.phaseSelect')}</option>
                  {phases.map((phase) => (
                    <option key={phase.id} value={phase.id?.toString()}>
                        {getPhaseLabel(phase, phases)}
                    </option>
                  ))}
                </select>
                <IconChevronDown />
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>{t('create.fields.releaseOptional')}</label>
              <div className={styles.selectField}>
                <select
                  className={styles.select}
                  value={formData.releaseId}
                  onChange={(e) => handleInputChange('releaseId', e.target.value)}
                  disabled={isLoadingReleases}
                >
                  <option value="">{t('create.fields.noReleaseSelected')}</option>
                  {projectReleases.map((release) => (
                    <option key={release.id} value={release.id?.toString()}>
                      {release.name} {release.releaseDate ? `- ${release.releaseDate}` : ''}
                    </option>
                  ))}
                </select>
                <IconChevronDown />
              </div>
              {isLoadingReleases && (
                <div className={styles.helperText}>{t('create.fields.loadingReleases')}</div>
              )}
            </div>

            <div className={styles.inputGroup}>
              <label className={`${styles.inputLabel} ${styles.required}`}>{t('create.fields.criticality').replace(' *', '')}</label>
              <div className={styles.selectField}>
                <select
                  className={styles.select}
                  value={formData.criticality}
                  onChange={(e) => handleInputChange('criticality', e.target.value)}
                >
                  {criticalityOptions.map(criticality => (
                    <option key={criticality} value={criticality}>
                      {criticality === 'EASY' ? t('criticality.easy') : criticality === 'MIDDLE' ? t('criticality.middle') : criticality === 'HIGH' ? t('criticality.high') : criticality}
                    </option>
                  ))}
                </select>
                <IconChevronDown />
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label className={`${styles.inputLabel} ${styles.required}`}>{t('create.fields.dateRange')}</label>
              <div className={styles.dateInputRow}>
                <div className={styles.dateInput}>
                  <input
                    type="date"
                    className={styles.input}
                    placeholder={t('create.fields.startDatePlaceholder')}
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    min={selectedCustomPhase?.startTime || undefined}
                    max={selectedCustomPhase?.endTime || undefined}
                  />
                </div>

                <div className={styles.dateInput}>
                  <input
                    type="date"
                    className={styles.input}
                    placeholder={t('create.fields.endDatePlaceholder')}
                    value={formData.endDate}
                    onChange={(e) => {
                      if (formData.startDate && e.target.value < formData.startDate) {
                        showError(t('create.validation.error'), t('create.validation.startBeforeEnd'));
                        return;
                      }
                      handleInputChange('endDate', e.target.value);
                    }}
                    min={formData.startDate || selectedCustomPhase?.startTime || undefined}
                    max={selectedCustomPhase?.endTime || undefined}
                  />
                </div>
              </div>
            </div>
            <div className={styles.inputGroup}>
              <div className={styles.checkboxWrapper}>
                <input
                  type="checkbox"
                  id="isExtended"
                  className={styles.checkboxInput}
                  checked={formData.isExtended}
                  onChange={(e) => handleInputChange('isExtended', e.target.checked)}
                />
                <label htmlFor="isExtended" className={styles.checkboxLabel}>{t('create.fields.extendedDeliverable')}</label>
              </div>
            </div>
              </>
            ) : (
              <>
                {presetStep === 'phase' && (
                  <>
                    <div className={styles.inputGroup}>
                      <label className={`${styles.inputLabel} ${styles.required}`}>{t('create.fields.selectPhase')}</label>
                      <div className={styles.selectField}>
                        <select
                          className={styles.select}
                          value={selectedPhaseType}
                          onChange={(e) => setSelectedPhaseType(e.target.value as PhaseType)}
                        >
                          <option value="">{t('create.fields.choosePhase')}</option>
                          {phases.map((phase) => (
                            <option key={phase.id} value={phase.type}>
                                {getPhaseLabel(phase, phases)}
                            </option>
                          ))}
                        </select>
                        <IconChevronDown />
                      </div>
                      <div className={styles.helperText}>
                        {t('create.fields.selectPhaseHelper')}
                      </div>
                    </div>

                    <div className={styles.inputGroup}>
                      <div className={styles.checkboxWrapper}>
                        <input
                          type="checkbox"
                          id="isExtendedCatalog"
                          className={styles.checkboxInput}
                          checked={isExtendedCatalog}
                          onChange={(e) => setIsExtendedCatalog(e.target.checked)}
                        />
                        <label htmlFor="isExtendedCatalog" className={styles.checkboxLabel}>
                          {t('create.fields.extendedCatalog')}
                        </label>
                      </div>
                      <div className={styles.helperText}>
                        {t('create.fields.extendedCatalogHelper')}
                      </div>
                    </div>
                  </>
                )}

                {presetStep === 'deliverable' && (
                  <>
                    <div className={styles.stepInfo}>
                        <strong>{t('create.stepInfo.phase')}</strong> {selectedPhaseLabel} {isExtendedCatalog && `| ${t('create.stepInfo.extended')}`}
                    </div>
                    <div className={styles.inputGroup}>
                      <label className={`${styles.inputLabel} ${styles.required}`}>{t('create.fields.selectTemplate')}</label>
                      <div className={styles.selectField}>
                        <select
                          className={styles.select}
                          value={selectedDeliverableName}
                          onChange={(e) => setSelectedDeliverableName(e.target.value)}
                          disabled={isLoadingCatalog}
                        >
                          <option value="">{t('create.fields.chooseDeliverable')}</option>
                          {uniqueDeliverableNames.map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </select>
                        <IconChevronDown />
                      </div>
                      {isLoadingCatalog && (
                        <div className={styles.helperText}>{t('create.fields.loadingTemplates')}</div>
                      )}
                      {!isLoadingCatalog && uniqueDeliverableNames.length === 0 && (
                        <div className={styles.helperText}>{t('create.fields.noTemplates')}</div>
                      )}
                    </div>
                  </>
                )}

                {presetStep === 'dates' && (
                  <>
                    <div className={styles.stepInfo}>
                        <strong>{t('create.stepInfo.phase')}</strong> {selectedPhaseLabel} {isExtendedCatalog && `| ${t('create.stepInfo.extended')}`} | <strong>{t('create.stepInfo.deliverable')}</strong> {selectedDeliverableName}
                    </div>
                    <div className={styles.inputGroup}>
                      <label className={`${styles.inputLabel} ${styles.required}`}>{t('create.fields.deliverableDateRange')}</label>
                      <div className={styles.dateInputRow}>
                        <div className={styles.dateInput}>
                          <input
                            type="date"
                            className={styles.input}
                            placeholder={t('create.fields.startDatePlaceholder')}
                            value={presetDates.startDate}
                            onChange={(e) => setPresetDates(prev => ({ ...prev, startDate: e.target.value }))}
                            min={selectedPresetPhase?.startTime || undefined}
                            max={selectedPresetPhase?.endTime || undefined}
                          />
                        </div>
                        <div className={styles.dateInput}>
                          <input
                            type="date"
                            className={styles.input}
                            placeholder={t('create.fields.endDatePlaceholder')}
                            value={presetDates.endDate}
                            onChange={(e) => {
                              if (presetDates.startDate && e.target.value < presetDates.startDate) {
                                showError(t('create.validation.error'), t('create.validation.startBeforeEnd'));
                                return;
                              }
                              setPresetDates(prev => ({ ...prev, endDate: e.target.value }));
                            }}
                            min={presetDates.startDate || selectedPresetPhase?.startTime || undefined}
                            max={selectedPresetPhase?.endTime || undefined}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {presetStep === 'criticality' && (
                  <>
                    <div className={styles.stepInfo}>
                        <strong>{t('create.stepInfo.phase')}</strong> {selectedPhaseLabel} {isExtendedCatalog && `| ${t('create.stepInfo.extended')}`} | <strong>{t('create.stepInfo.deliverable')}</strong> {selectedDeliverableName}
                    </div>
                    <div className={styles.inputGroup}>
                      <label className={`${styles.inputLabel} ${styles.required}`}>{t('create.fields.criticality').replace(' *', '')}</label>
                      <div className={styles.selectField}>
                        <select
                          className={styles.select}
                          value={presetCriticality}
                          onChange={(e) => setPresetCriticality(e.target.value as DeliverObjectCriticality)}
                        >
                          {criticalityOptions.map((option) => (
                            <option key={option} value={option}>
                              {option === 'EASY' ? t('criticality.easy') : option === 'MIDDLE' ? t('criticality.middle') : t('criticality.high')}
                            </option>
                          ))}
                        </select>
                        <IconChevronDown />
                      </div>
                      <div className={styles.helperText}>
                        {t('create.fields.criticalityHelper')}
                      </div>
                    </div>
                  </>
                )}

                {presetStep === 'review' && (
                  <>
                    <div className={styles.stepInfo}>
                        <strong>{t('create.stepInfo.phase')}</strong> {selectedPhaseLabel} {isExtendedCatalog && `| ${t('create.stepInfo.extended')}`} | <strong>{t('create.stepInfo.deliverable')}</strong> {selectedDeliverableName}
                    </div>
                    <div className={styles.presetReview}>
                      <div className={styles.reviewHeader}>
                        <IconClipboardList size={24} className={styles.reviewIcon} />
                        <h4 className={styles.reviewTitle}>{t('create.review.title')}</h4>
                      </div>

                      <div className={styles.deliverablePeriod}>
                        <div className={styles.periodItem}>
                          <IconCalendar size={20} className={styles.periodIcon} />
                          <div className={styles.periodContent}>
                            <span className={styles.periodLabel}>{t('create.review.deliverablePeriod')}</span>
                            <span className={styles.periodDates}>
                              {formatDisplayDate(presetDates.startDate)} → {formatDisplayDate(presetDates.endDate)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className={styles.tasksOverview}>
                        <div className={styles.overviewBadge}>
                          <IconChecklist size={18} />
                          <span>{t('create.review.taskCount', { count: filteredCatalogItems.length })}</span>
                        </div>
                      </div>
                      
                      <div className={styles.tasksList}>
                        {filteredCatalogItems
                          .filter(item => catTaskName(item) && catValidationCriteria(item) && catAcceptanceCriteria(item))
                          .map((item, index) => (
                          <div key={item.id || index} className={styles.taskCard}>
                            <div className={styles.taskCardHeader}>
                              <div className={styles.taskNumber}>{index + 1}</div>
                                <h5 className={styles.taskName}>{catTaskName(item)?.replaceAll('_', ' ')}</h5>
                            </div>

                            <div className={styles.taskCardBody}>
                              {catValidationCriteria(item) && (
                                <div className={styles.criteriaItem}>
                                  <div className={styles.criteriaLabel}>
                                    <IconCheck size={16} />
                                    <span>{t('create.review.validationCriteria')}</span>
                                  </div>
                                  <p className={styles.criteriaText}>{catValidationCriteria(item)}</p>
                                </div>
                              )}

                              {catAcceptanceCriteria(item) && (
                                <div className={styles.criteriaItem}>
                                  <div className={styles.criteriaLabel}>
                                    <IconChecklist size={16} />
                                    <span>{t('create.review.acceptanceCriteria')}</span>
                                  </div>
                                  <p className={styles.criteriaText}>{catAcceptanceCriteria(item)}</p>
                                </div>
                              )}

                              {catValidationMethods(item) && (
                                <div className={styles.criteriaItem}>
                                  <div className={styles.criteriaLabel}>
                                    <IconClipboardList size={16} />
                                    <span>{t('create.review.validationMethod')}</span>
                                  </div>
                                  <p className={styles.criteriaText}>{catValidationMethods(item)}</p>
                                </div>
                              )}

                                {catResponsibleRoles(item) && (
                                <div className={styles.criteriaItem}>
                                  <div className={styles.criteriaLabel}>
                                    <IconUserCheck size={16} />
                                    <span>{t('create.review.responsibleRole')}</span>
                                  </div>
                                    <p className={styles.criteriaText}>{catResponsibleRoles(item)}</p>
                                </div>
                              )}

                              <div className={styles.validationDateSection}>
                                <label className={styles.dateLabel}>
                                  <IconCalendarEvent size={18} />
                                    <span>{t('create.review.validationDate')}</span>
                                </label>
                                <input
                                  type="date"
                                  className={styles.dateInput}
                                  value={taskValidationDates[item.id?.toString() || ''] || ''}
                                  onChange={(e) => setTaskValidationDates(prev => ({
                                    ...prev,
                                      [item.id?.toString() || '']: e.target.value
                                  }))}
                                  min={presetDates.startDate}
                                  max={presetDates.endDate}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className={styles.popupFooter}>
          <div className={styles.footerActions}>
            <button className={styles.cancelButton} onClick={handleCancel}>
              {t('create.buttons.cancel')}
            </button>

            {mode === 'custom' ? (
              <button
                className={`${styles.submitButton} ${isFormValid() ? styles.submitButtonActive : ''}`}
                onClick={handleSubmit}
                disabled={!isFormValid()}
              >
                <IconPlus />
                {t('create.buttons.createItem')}
              </button>
            ) : (
              <>
                {presetStep === 'phase' && (
                  <button
                    className={`${styles.submitButton} ${selectedPhaseType ? styles.submitButtonActive : ''}`}
                    onClick={() => setPresetStep('deliverable')}
                    disabled={!selectedPhaseType}
                  >
                    {t('create.buttons.nextSelectTemplate')}
                  </button>
                )}
                {presetStep === 'deliverable' && (
                  <>
                    <button
                      className={styles.backButton}
                      onClick={() => setPresetStep('phase')}
                    >
                      {t('create.buttons.back')}
                    </button>
                    <button
                      className={`${styles.submitButton} ${selectedDeliverableName ? styles.submitButtonActive : ''}`}
                      onClick={() => setPresetStep('dates')}
                      disabled={!selectedDeliverableName}
                    >
                      {t('create.buttons.nextSetDates')}
                    </button>
                  </>
                )}
                {presetStep === 'dates' && (
                  <>
                    <button
                      className={styles.backButton}
                      onClick={() => setPresetStep('deliverable')}
                    >
                      {t('create.buttons.back')}
                    </button>
                    <button
                      className={`${styles.submitButton} ${(presetDates.startDate && presetDates.endDate) ? styles.submitButtonActive : ''}`}
                      onClick={() => setPresetStep('criticality')}
                      disabled={!presetDates.startDate || !presetDates.endDate}
                    >
                      {t('create.buttons.nextSetCriticality')}
                    </button>
                  </>
                )}
                {presetStep === 'criticality' && (
                  <>
                    <button
                      className={styles.backButton}
                      onClick={() => setPresetStep('dates')}
                    >
                      {t('create.buttons.back')}
                    </button>
                    <button
                      className={`${styles.submitButton} ${styles.submitButtonActive}`}
                      onClick={() => setPresetStep('review')}
                    >
                      {t('create.buttons.nextReview')}
                    </button>
                  </>
                )}
                {presetStep === 'review' && (
                  <>
                    <button
                      className={styles.backButton}
                      onClick={() => setPresetStep('criticality')}
                    >
                      {t('create.buttons.back')}
                    </button>
                    <button
                      className={`${styles.submitButton} ${styles.submitButtonActive}`}
                      onClick={handlePresetCreate}
                      disabled={isCreatingPreset}
                    >
                      {isCreatingPreset ? t('create.buttons.creating') : t('create.buttons.create')}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateDeliverablePopup;
